import { Component, Input, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { forkJoin } from 'rxjs';
import { EditProfileDialogComponent } from '../edit-profile-dialog/edit-profile-dialog.component';
import { ActivityConfigService } from '../services/activity-config.service';
import { Activity, ActivityIndicatorConfig, Indicator, RECALCULATION_STATUS } from '../model/activity-config.model';
import { NotifService } from 'src/app/core/notification/notif.service';
import { UserStateService } from 'src/app/auth/user-state-service';
import { take } from 'rxjs/operators';
import { DialogComponent } from 'src/app/shared/dialog/dialog.component';
import { DialogModel } from 'src/app/model/dialog/dialog-model';

@Component({
    selector: 'consolidated-risk-score-list',
    templateUrl: './consolidated-risk-score-list.component.html',
    styleUrls: ['./consolidated-risk-score-list.component.scss']
})
export class ConsolidatedRiskScoreListComponent implements OnInit {

    /** The profile type that can never be deleted (FR-1.9), matched by name as the backend does. */
    private static readonly NOT_APPLICABLE = 'NOT APPLICABLE';
    private static readonly ERROR_LOAD_DATA = 'Failed to load consolidated risk score data';
    private static readonly ERROR_DELETE = 'Failed to delete. Please try again.';
    private static readonly SUCCESS_DELETE = 'The vulnerability profile type has been deleted';
    private static readonly SUCCESS_UPDATE = 'The consolidated risk score will be updated for all locations in approximately 24 hours';

    @Input() accountId: number;

    indicators: Indicator[] = [];
    savedProfiles: any[] = [];
    displayedColumns: string[] = [];
    errorMessage: string = '';
    isLoading: boolean = false;

    constructor(
        private dialog: MatDialog,
        private readonly activityConfigService: ActivityConfigService,
        private readonly notifService: NotifService,
        private readonly userStateService: UserStateService,
    ) {}

    /**
     * Who may configure vulnerability profile types: Super Admin and Client Admin.
     * A Client Member is excluded. Read through to the service rather than copied,
     * since the flags change on login/logout. Both checks are needed - isAccountAdmin() is
     * built from account membership, so it is undefined for a Super Admin who is not a
     * member of this account.
     */
    get canConfigure(): boolean {
        return this.userStateService.isSuperadmin || !!this.userStateService.isAccountAdmin(this.accountId);
    }

    ngOnInit() {
        this.loadTableData();
    }

    private loadTableData() {
        this.isLoading = true;
        this.errorMessage = '';
        forkJoin({
            activities: this.activityConfigService.getActivities(),
            indicators: this.activityConfigService.getIndicators(),
            configs: this.activityConfigService.getAllConfigs(this.accountId),
        }).subscribe({
            next: ({ activities, indicators, configs }) => {
                this.indicators = indicators;
                this.displayedColumns = ['vulnerabilityType', ...indicators.map(indicator => indicator.code), 'updatedOn', 'actions'];
                this.savedProfiles = this.buildProfileRows(activities, configs);
                this.isLoading = false;
            },
            error: (error) => {
                this.errorMessage = ConsolidatedRiskScoreListComponent.ERROR_LOAD_DATA;
                this.isLoading = false;
            },
        });
    }

    private buildProfileRows(activities: Activity[], configs: ActivityIndicatorConfig[]): any[] {
        const configByActivityId = new Map<number, ActivityIndicatorConfig>();
        configs.forEach(config => configByActivityId.set(config.activityId, config));

        return activities.map(activity => {
            const row: any = {
                activityId: activity.id,
                vulnerabilityType: activity.name,
                status: configByActivityId.get(activity.id)?.status,
                updatedOn: configByActivityId.get(activity.id)?.updatedOn,
            };
            const config = configByActivityId.get(activity.id);
            const valueByIndicatorId = new Map<number, number>();
            (config?.indicatorValues || []).forEach(indicatorValue => valueByIndicatorId.set(indicatorValue.indicatorId, indicatorValue.value));

            this.indicators.forEach(indicator => {
                const decimalValue = valueByIndicatorId.get(indicator.id);
                // Backend stores/uses the decimal coefficient (0.05); the table and dialog
                // show the percentage a person actually enters (5), per FR-1.7.
                row[indicator.code] = decimalValue !== undefined ? decimalValue * 100 : null;
            });
            return row;
        });
    }

    addNewProfile() {
        if (!this.canConfigure) {
            return;
        }
        const dialogRef = this.dialog.open(EditProfileDialogComponent, {
            width: '1200px',
            maxWidth: '95vw',
            maxHeight: '90vh',
            data: { isEditMode: false, indicators: this.indicators, accountId: this.accountId }
        });

        dialogRef.afterClosed().subscribe({
            next: (result) => {
                if (result && result.vulnerabilityType) {
                    this.savedProfiles = [...this.savedProfiles, result];
                    this.showConfirmation();
                }
            },
            error: (error) => {}
        });
    }

    editProfile(profile: any) {
        if (!this.canConfigure || this.isCalculationInProgress(profile)) {
            return;
        }
        const dialogRef = this.dialog.open(EditProfileDialogComponent, {
            width: '1200px',
            maxWidth: '95vw',
            maxHeight: '90vh',
            data: { isEditMode: true, profile, indicators: this.indicators, accountId: this.accountId }
        });

        dialogRef.afterClosed().subscribe({
            next: (result) => {
                if (result) {
                    const index = this.savedProfiles.findIndex(p => p.activityId === result.activityId);
                    if (index !== -1) {
                        // Reassign to a new array (not an in-place mutation) - the CDK table only
                        // re-renders when the [dataSource] reference itself changes.
                        const updated = [...this.savedProfiles];
                        updated[index] = result;
                        this.savedProfiles = updated;
                    }
                    if (result.status === RECALCULATION_STATUS.QUEUED) {
                        this.showConfirmation();
                    }
                }
            },
            error: (error) => {}
        });
    }

    /**
     * Deletes the profile type outright (FR-1.20/1.21): its percentages and stored scores on
     * every dataset that holds them, plus the profile type itself. It is master data shared
     * across accounts, so the confirmation says so - this removes it everywhere, not just
     * from this account.
     */
    /**
     * NOT APPLICABLE is never deletable, whether or not anything is assigned to it (FR-1.9) - it
     * is the profile type for locations that carry no relevant activity. The backend refuses it
     * too; this only keeps the action from being offered.
     */
    isDeletable(profile: any): boolean {
        return !this.isCalculationInProgress(profile)
            && !ConsolidatedRiskScoreListComponent.isNotApplicable(profile);
    }

    deleteTooltip(profile: any): string {
        if (this.isCalculationInProgress(profile)) {
            return 'Calculation in progress';
        }
        if (ConsolidatedRiskScoreListComponent.isNotApplicable(profile)) {
            return `${ConsolidatedRiskScoreListComponent.NOT_APPLICABLE} cannot be deleted`;
        }
        return 'Delete Profile';
    }

    private static isNotApplicable(profile: any): boolean {
        return (profile?.vulnerabilityType || '').trim().toUpperCase()
            === ConsolidatedRiskScoreListComponent.NOT_APPLICABLE;
    }

    deleteProfile(profile: any) {
        if (!this.canConfigure || !this.isDeletable(profile)) {
            return;
        }
        // A type still assigned to locations cannot be deleted, so that is checked
        // before the confirmation is offered rather than after it is accepted.
        this.activityConfigService.countAssignedLocations(this.accountId, profile.activityId).subscribe({
            next: (locationCount) => {
                if (locationCount > 0) {
                    this.showDeletionBlocked(profile, locationCount);
                } else {
                    this.confirmDeletion(profile);
                }
            },
            error: (error) => {
                this.notifService.error(error?.error?.errors || ConsolidatedRiskScoreListComponent.ERROR_DELETE);
            },
        });
    }

    private showDeletionBlocked(profile: any, locationCount: number) {
        this.dialog.open(DialogComponent, {
            data: new DialogModel(
                'Cannot Delete',
                `${profile?.vulnerabilityType || 'This vulnerability profile type'} is assigned to ${locationCount}`
                + ` location${locationCount === 1 ? '' : 's'}, so it cannot be deleted.`
                + ' A location\'s vulnerability profile type is set when it is onboarded.',
                undefined, 'OK', true
            ),
        });
    }

    private confirmDeletion(profile: any) {
        this.dialog.open(DialogComponent, {
            data: new DialogModel(
                'Confirm Action',
                // Only reached when no location is assigned, so there are no stored scores to mention.
                `Are you sure you want to delete the ${profile?.vulnerabilityType || 'selected'} vulnerability profile type?`
                + ' This removes the profile type and its configuration.'
                + ' It is removed for every account, not only this one.'
            ),
        })
            .afterClosed()
            .pipe(take(1))
            .subscribe({
                next: (confirmed) => {
                    if (confirmed) {
                        this.removeProfileConfiguration(profile);
                    }
                },
                error: (error) => {}
            });
    }

    private removeProfileConfiguration(profile: any) {
        this.activityConfigService.deleteConfig(this.accountId, profile.activityId).subscribe({
            next: () => {
                // Reassign rather than mutate - the CDK table only re-renders when the
                // [dataSource] reference itself changes.
                this.savedProfiles = this.savedProfiles.filter(p => p.activityId !== profile.activityId);
                this.notifService.success(ConsolidatedRiskScoreListComponent.SUCCESS_DELETE);
            },
            error: (error) => {
                this.notifService.error(error?.error?.errors || ConsolidatedRiskScoreListComponent.ERROR_DELETE);
            },
        });
    }

    isCalculationInProgress(profile: any): boolean {
        return profile?.status === RECALCULATION_STATUS.QUEUED || profile?.status === RECALCULATION_STATUS.IN_PROGRESS;
    }

    showConfirmation() {
        this.notifService.success(ConsolidatedRiskScoreListComponent.SUCCESS_UPDATE);
    }
}
